import {
	useContext,
	useEffect,
	useRef,
	useState,
	useCallback,
	useMemo,
	useLayoutEffect,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as Highcharts from 'highcharts';
import { NWLContext } from '../../../contexts';
import { getIconUrl, roundTo, testIdFormatter } from '../../../utils';
import { getHistoricalReadings, getForecastReadings } from '@/services/readings';
import { DateTimePicker, assetModalShortCuts } from '../Common/datetimepicker';
import './assetinfo.scss';
import { ShortCut } from '../Common/datetimepicker/DateTimeTypes';
import Chart from '../Common/Chart/Chart';
import ASSET_ITEM_LABELS from './assetInfo.model';
import { capitaliseWord } from '../../../utils';
import { useActiveLocationId } from '../../utils/activeLocation';
import { nwlKeys } from '../../utils/Utils';

dayjs.extend(utc);

const NO_DATA_KEY = 'Blank';
const FORECAST_KEY = 'Forecast';

const hslStrToArr = (hslColor, alphaValue) => {
	const arr = hslColor.replace(/(\s|%|hsl|\(|\))/gim, '').split(',');
	return alphaValue ? [...arr, alphaValue] : arr;
};
const hslArrToStr = (hslColorArr) => {
	const hasAlpha = hslColorArr.length === 4;
	return `hsl${hasAlpha ? 'a' : ''}(${hslColorArr[0]},${hslColorArr[1]}%,${hslColorArr[2]}%${hasAlpha ? `,${hslColorArr[3]}` : ''})`;
};

const hslLightenForGradient = (hslColor, alphaValue?) => {
	const values = hslStrToArr(hslColor, alphaValue);
	const adjustedValue = Object.assign(
		[],
		values,
		{ 1: Number(values[1]) - 23 },
		{ 2: Number(values[2]) + 30 },
	);
	const response = hslArrToStr(adjustedValue);
	return response;
};

export const extractHistoricValuesToData = (historicData, sourceKey) => {
	const result: Record<string, [number, number | null][]> = {
		[NO_DATA_KEY]: [],
	};

	if (!Array.isArray(historicData) || historicData.length === 0) {
		return result;
	}

	const actualKey = sourceKey || 'NWL';

	for (const point of historicData) {
		if (!point || typeof point.time === 'undefined') continue;

		const tsNum = Number(point.time);
		if (!Number.isFinite(tsNum)) continue;

		const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

		const value = point.value;
		const dataPoint: [number, number | null] = [tsMs, value];

		if (value == null) {
			result[NO_DATA_KEY].push(dataPoint);
		} else {
			if (!result[actualKey]) {
				result[actualKey] = [];
			}
			result[actualKey].push(dataPoint);
		}
	}

	return result;
};

export const extractForecastData = (forecastData: any) => {
	if (!Array.isArray(forecastData) || forecastData.length === 0) return {};

	const byTime = new Map<number, number[]>();

	for (const point of forecastData) {
		if (!point || typeof point.time === 'undefined') continue;

		const tsNum = Number(point.time);
		if (!Number.isFinite(tsNum)) continue;
		const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

		const raw = point.value;
		if (raw === null || raw === undefined || raw === '') continue;

		const vNum = Number(raw);
		if (!Number.isFinite(vNum)) continue;

		const arr = byTime.get(tsMs) ?? [];
		arr.push(vNum);
		byTime.set(tsMs, arr);
	}

	if (byTime.size === 0) return {};

	const series: any[] = [];
	for (const [tsMs, values] of byTime.entries()) {
		const min = Math.min(...values);
		const max = Math.max(...values);
		const mid = (min + max) / 2;

		series.push({ x: tsMs, y: mid, custom: { range: [min, max] } });
	}

	series.sort((a, b) => a.x - b.x);

	return series.length ? { [FORECAST_KEY]: series } : {};
};

const dataLabelLookup = (label) => {
	const lowerCaseLabel = label.toLowerCase();
	if (lowerCaseLabel.includes('blank')) return 'No data';
	if (lowerCaseLabel.includes('nwl')) return 'Actual data';
	if (lowerCaseLabel.includes('dt')) return 'Virtual data';
	if (lowerCaseLabel.includes('forecast')) return 'Forecast data';
	return label;
};

export const formatSeriesData = (data, hslColorArr) =>
	Object.keys(data).map((source, i) => {
		const index = i % hslColorArr.length;
		const primaryColor = hslColorArr[index];
		const gradientColor = hslLightenForGradient(hslColorArr[index]);
		const readableSourceName = dataLabelLookup(source);
		const isForecast = source.toLowerCase().includes('forecast');
		const isBlank = source === NO_DATA_KEY;

		return {
			name: isBlank ? 'No data' : readableSourceName,
			type: isForecast ? 'line' : 'area',
			data: data[source],
			dashStyle: isForecast ? 'ShortDot' : 'Solid',
			lineWidth: 2,
			lineColor: isBlank ? 'rgba(0,0,0,0)' : primaryColor,
			marker: isBlank
				? { enabled: false }
				: { lineWidth: 1, lineColor: primaryColor, fillColor: gradientColor },
			fillColor:
				isBlank || isForecast
					? undefined
					: {
							linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
							stops: [
								[0, gradientColor],
								[1, gradientColor],
							],
						},
			showInLegend: !isBlank,
			enableMouseTracking: !isBlank,
		};
	});

const ModalHeader = ({ selectedAssetItem, onModalClose, iconUrl }) => {
	const title =
		typeof selectedAssetItem?.name === 'string'
			? selectedAssetItem.name.charAt(0).toUpperCase() + selectedAssetItem.name.slice(1)
			: '';
	return (
		<div className="flex items-center justify-between rounded-t py-3 px-5 border-b">
			<img src={iconUrl} className="w-8 h-8 mr-1" />
			<h3 className="text-xl font-semibold text-gray-900" id="asset-name">
				{title}
			</h3>
			<button
				onClick={onModalClose}
				type="button"
				className="custom-modal-close-btn ms-auto"
				data-modal-hide="default-modal"
				data-testid="asset-modal-close-button"
				id="asset-modal-close-button"
			>
				<svg
					className="w-3 h-3"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 14 14"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
					/>
				</svg>
				<span className="sr-only">Close modal</span>
			</button>
		</div>
	);
};

interface ModalRowContainer {
	heading?: string;
	children: JSX.Element[];
}

const ModalRowContainer = ({
	heading,
	children,
}: {
	heading?: string;
	children: JSX.Element[];
}) => {
	return (
		<div className="mx-5 py-5 border-b">
			{heading && (
				<h5
					data-testid={testIdFormatter(heading, 'asset-modal-row-header')}
					id="asset-modal-row-header"
					className="font-bold mb-2"
				>
					{heading}
				</h5>
			)}
			<div className="space-y-3">{children}</div>
		</div>
	);
};

const AssetValuePair = ({
	keyText,
	value = 'N/A',
}: {
	keyText: string;
	value?: string | number;
}) => {
	return (
		<div className="grid grid-cols-12">
			<span
				data-testid={testIdFormatter(keyText, 'asset-pair-key')}
				id="asset-pair-key"
				className="col-span-5 text-[14px] text-gray-500"
			>
				{keyText}
			</span>
			<span
				data-testid={testIdFormatter(keyText, 'asset-pair-value')}
				id="asset-pair-value"
				className="col-span-7 font-semibold text-gray-700"
			>
				{value}
			</span>
		</div>
	);
};

const CapacityCard = ({
	big,
	label,
	idPrefix,
}: {
	big: string;
	label: string;
	idPrefix: string;
}) => (
	<div className="py-2">
		<div className="text-[24px] font-bold" id={`${idPrefix}-value`}>
			{big}
		</div>
		<div className="text-gray-500 text-[14px]" id={`${idPrefix}-label`}>
			{label}
		</div>
	</div>
);

const toNumber = (v: any): number | undefined => {
	if (v === null || v === undefined || v === '') return undefined;
	const n = Number(v);
	return Number.isFinite(n) ? n : undefined;
};

const formatIfUnsure = (v: number | undefined, unit: string) =>
	typeof v === 'number' && Number.isFinite(v) ? `${roundTo(v, 2)} ${unit}` : 'N/A';

const calculateDateRange = () => {
	const currentMinutes = dayjs().minute();
	//NOTE: We're rounding down the minutes to the nearest 5 minutes
	const sanitizedStartMinutes = currentMinutes - (currentMinutes % 5);
	const startOfDay = dayjs()
		.subtract(3, 'day')
		.minute(sanitizedStartMinutes)
		.second(0)
		.millisecond(0);

	//NOTE: We're rounding down the minutes to the nearest 15 minutes
	const sanitizedEndMinutes = currentMinutes - (currentMinutes % 15);
	const endOfDay = dayjs().add(12, 'hour').minute(sanitizedEndMinutes).second(0).millisecond(0);

	const shortCuts = assetModalShortCuts;
	if (shortCuts.filter((shortcut: ShortCut) => shortcut.label === 'Default').length === 0) {
		shortCuts.unshift({ label: 'Default', value: [startOfDay, endOfDay] });
	}

	return { shortCuts, startOfDay, endOfDay };
};

const determineMinDateTimestamp = (data: any) => {
	if (!data || data.length === 0) {
		return null;
	}

	const timestamps = data.flatMap((item: any) => {
		if (Array.isArray(item)) return item[0];
		if (item && typeof item.x === 'number') return item.x;
		return [];
	});

	if (!timestamps.length) return null;

	return Number(timestamps.reduce((a: number, b: number) => Math.min(a, b)));
};

const determineMaxDateTimestamp = (data: any) => {
	if (!data || data.length === 0) {
		return null;
	}

	const timestamps = data.flatMap((item: any) => {
		if (Array.isArray(item)) return item[0];
		if (item && typeof item.x === 'number') return item.x;
		return [];
	});

	if (!timestamps.length) return null;

	return Number(timestamps.reduce((a: number, b: number) => Math.max(a, b)));
};

const checkDataWithinDateRange = (data: any) => {
	const min = determineMinDateTimestamp(data[NO_DATA_KEY]);
	const max = determineMaxDateTimestamp(data[FORECAST_KEY]);
	return { min, max };
};

const ChartNotice = ({
	variant,
	text,
	onClose,
}: {
	variant: 'none' | 'no_forecast' | 'engine_fails';
	text?: string;
	onClose: () => void;
}) => {
	if (variant === 'none') return null;

	return (
		<div
			role="status"
			aria-live="polite"
			className="bg-gray-100'rounded-md px-3 py-2 text-[#535662] items-start gap-2 shadow-sm pt-5 p-2"
			style={{
				position: 'absolute',
				background: '#EBEEF1B2',
				top: 20,
				right: 8,
				justifyContent: 'middle',
				maxWidth: 90,
				minHeight: 235,
				zIndex: 5,
			}}
		>
			<button
				onClick={onClose}
				type="button"
				className="custom-modal-close-btn bg-transparent absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center"
				data-modal-hide="default-modal"
				data-testid="asset-modal-close-button"
				id="asset-modal-close-button"
			>
				<svg
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 14 14"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
					/>
				</svg>
				<span className="sr-only">Close modal</span>
			</button>
			<svg
				className="mb-1"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M12 14C11.7528 14 11.5111 14.0733 11.3055 14.2107C11.1 14.348 10.9398 14.5432 10.8452 14.7716C10.7505 15.0001 10.7258 15.2514 10.774 15.4939C10.8223 15.7363 10.9413 15.9591 11.1161 16.1339C11.2909 16.3087 11.5137 16.4277 11.7561 16.476C11.9986 16.5242 12.25 16.4995 12.4784 16.4049C12.7068 16.3102 12.902 16.15 13.0393 15.9445C13.1767 15.7389 13.25 15.4972 13.25 15.25C13.25 14.9185 13.1183 14.6005 12.8839 14.3661C12.6495 14.1317 12.3315 14 12 14ZM12 12.5C12.2652 12.5 12.5196 12.3946 12.7071 12.2071C12.8946 12.0196 13 11.7652 13 11.5V8.5C13 8.23478 12.8946 7.98043 12.7071 7.79289C12.5196 7.60536 12.2652 7.5 12 7.5C11.7348 7.5 11.4804 7.60536 11.2929 7.79289C11.1054 7.98043 11 8.23478 11 8.5V11.5C11 11.7652 11.1054 12.0196 11.2929 12.2071C11.4804 12.3946 11.7348 12.5 12 12.5ZM12 2C10.0222 2 8.08879 2.58649 6.4443 3.6853C4.79981 4.78412 3.51809 6.3459 2.76121 8.17317C2.00433 10.0004 1.8063 12.0111 2.19215 13.9509C2.578 15.8907 3.53041 17.6725 4.92894 19.0711C6.32746 20.4696 8.10929 21.422 10.0491 21.8079C11.9889 22.1937 13.9996 21.9957 15.8268 21.2388C17.6541 20.4819 19.2159 19.2002 20.3147 17.5557C21.4135 15.9112 22 13.9778 22 12C21.9971 9.34873 20.9426 6.80688 19.0679 4.93215C17.1931 3.05742 14.6513 2.00291 12 2ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C19.9974 14.1209 19.1537 16.1542 17.6539 17.6539C16.1542 19.1536 14.1209 19.9974 12 20Z"
					fill="#434B56"
				/>
			</svg>

			<div className="text-[12px] leading-snug font-medium" id={`${variant}-alert`}>
				{text}
			</div>
		</div>
	);
};

const isValidRange = (r: any): r is [Dayjs, Dayjs] =>
	Array.isArray(r) &&
	r.length === 2 &&
	dayjs.isDayjs(r[0]) &&
	dayjs.isDayjs(r[1]) &&
	r[0].isValid() &&
	r[1].isValid();

const AssetInfo = ({
	fetchHistoricalReadings = getHistoricalReadings,
	fetchForecastReadings = getForecastReadings,
}) => {
	const { shortCuts, startOfDay, endOfDay } = useMemo(() => calculateDateRange(), []);
	const defaultRange = useMemo(
		() => [startOfDay, endOfDay] as [Dayjs, Dayjs],
		[startOfDay, endOfDay],
	);

	const {
		decisionEngine: {
			selectedAssetItem,
			setSelectedAssetItem,
			setShowComparisonGraph,
			dateRange,
			setDateRange,
		},
	}: any = useContext(NWLContext);
	const [notice, setNotice] = useState<{
		variant: 'none' | 'no_forecast' | 'engine_fails';
		text?: string;
		detailsUrl?: string | null;
	}>({ variant: 'none' });
	const [noticeDismissed, setNoticeDismissed] = useState(false);
	const [chartOptions, setChartOptions] = useState<Highcharts.Options>();
	const [loadingChart, setLoadingChart] = useState(false);
	const [chartError, setChartError] = useState(null);
	const colorArr = [
		'hsl(0, 0%, 100%)',
		'hsl(193, 88%, 30%)',
		'hsl(266, 100%, 64%)',
		'hsl(24, 88.20%, 30.00%)',
	];

	const [pickerRange, setPickerRange] = useState<[Dayjs, Dayjs]>(defaultRange);
	const locationId = useActiveLocationId();

	useLayoutEffect(() => {
		if (!selectedAssetItem) return;

		const next = isValidRange(dateRange) ? dateRange : defaultRange;

		setPickerRange(next);

		if (!isValidRange(dateRange)) {
			setDateRange(next);
		}
	}, [selectedAssetItem, dateRange, defaultRange, setDateRange]);

	const onModalClose = () => {
		setSelectedAssetItem(null);
	};

	// colours for threshold guide lines
	const COLOUR_AT_RISK = '#FE4D01';
	const COLOUR_IMPACTED = '#DB1428';

	const [thresholds, setThresholds] = useState<{
		atRiskHigh?: number;
		impactedHigh?: number;
		atRiskLow?: number;
		impactedLow?: number;
		unit?: string;
	}>({ unit: '' });

	const mt = selectedAssetItem?.measurementTarget ?? {};

	const currentValue = Number(
		mt?.currentValue ??
			mt?.dataSources?.lastReading?.value ??
			(Array.isArray(mt?.dataSources) ? mt.dataSources[0]?.lastReading?.value : undefined),
	);

	const thresholdCross = useMemo(() => {
		const arHigh =
			Number.isFinite(thresholds.atRiskHigh) &&
			Number.isFinite(currentValue) &&
			currentValue >= (thresholds.atRiskHigh as number);
		const arLow =
			Number.isFinite(thresholds.atRiskLow) &&
			Number.isFinite(currentValue) &&
			currentValue < (thresholds.atRiskLow as number);
		const imHigh =
			Number.isFinite(thresholds.impactedHigh) &&
			Number.isFinite(currentValue) &&
			currentValue >= (thresholds.impactedHigh as number);
		const imLow =
			Number.isFinite(thresholds.impactedLow) &&
			Number.isFinite(currentValue) &&
			currentValue < (thresholds.impactedLow as number);

		//TODO: bring back when discussion on display is concluded
		// const status = imHigh || imLow ? 'impacted' : arHigh || arLow ? 'at_risk' : null;
		const status = imHigh ? 'impacted' : arHigh ? 'at_risk' : null;

		return { arHigh, arLow, imHigh, imLow, status };
	}, [thresholds, currentValue]);

	const iconUrl = getIconUrl(selectedAssetItem?.type, 'ONLINE');

	const buildThresholdSeries = (
		name: 'At risk threshold' | 'Impacted threshold',
		y: number,
		showInLegend = true,
	): Highcharts.SeriesLineOptions => ({
		type: 'line',
		name,
		data: [
			[dateRange?.[0]?.valueOf?.() ?? dayjs().valueOf(), y],
			[dateRange?.[1]?.valueOf?.() ?? dayjs().add(1, 'hour').valueOf(), y],
		],
		enableMouseTracking: false,
		dashStyle: name === 'Impacted threshold' ? 'Dash' : 'DashDot',
		lineWidth: 1,
		marker: { enabled: false },
		states: { hover: { enabled: false } },
		color: name === 'Impacted threshold' ? COLOUR_IMPACTED : COLOUR_AT_RISK,
		showInLegend,
	});

	useEffect(() => {
		// Graph data
		const parameterId =
			selectedAssetItem?.measurementTarget?.measurementTargetId ??
			selectedAssetItem?.measurementTarget?.id;
		if (parameterId && dateRange) {
			const dateFrom = dateRange[0].unix().toString();
			const dateTo = dateRange[1].unix().toString();

			if (dateRange[0].isAfter(dateRange[1])) return;
			setLoadingChart(true);
			setChartError(null);

			try {
				const security = localStorage.getItem('security');
				const organizationId = JSON.parse(security ?? '{}')?.organizationId;

				if (!organizationId || !locationId) throw new Error('No organization/locations');

				const serialNumber = selectedAssetItem.measurementTarget?.dataSources?.[0]?.serialNumber;
				if (!serialNumber) return;

				const storedDtId = (
					localStorage.getItem(nwlKeys.dtId(organizationId, locationId)) ?? ''
				).trim();
				const storedSimId = (
					localStorage.getItem(nwlKeys.simId(organizationId, locationId)) ?? ''
				).trim();

				const now = dayjs();
				const shouldFetchForecast = Boolean(storedDtId && storedSimId);

				const historicalPromise = fetchHistoricalReadings(
					organizationId,
					locationId,
					serialNumber,
					dateFrom,
					dateTo,
				);

				const forecastPromise = shouldFetchForecast
					? fetchForecastReadings(
							organizationId,
							locationId,
							parameterId,
							storedDtId,
							storedSimId,
							now.unix().toString(),
							dateTo,
						)
					: Promise.resolve({ data: [] });

				Promise.all([historicalPromise, forecastPromise])
					.then(([historicalResponse, forecastResponse]) => {
						const historicalArray: Array<{ time: number; value: number | null }> = Array.isArray(
							historicalResponse?.data,
						)
							? historicalResponse.data
							: [];

						const forecastArray: Array<{
							time: number;
							value: string | number | null;
							iteration?: number | null;
						}> = Array.isArray(forecastResponse?.data) ? forecastResponse.data : [];

						const forecastHasValues =
							shouldFetchForecast &&
							forecastArray.some(
								(p) =>
									p &&
									p.value !== null &&
									p.value !== undefined &&
									p.value !== '' &&
									Number.isFinite(Number(p.value)),
							);

						const historicData = extractHistoricValuesToData(historicalArray, serialNumber);
						const forecastData = forecastHasValues ? extractForecastData(forecastArray) : {};

						const combinedData = {
							...historicData,
							...forecastData,
						};

						const { min, max } = checkDataWithinDateRange(combinedData);
						let isOutOfRangeStart = false;
						if (min) {
							const minTimestamp = dayjs(min);
							if (minTimestamp.isSame(dateRange[0]) || minTimestamp.isBefore(dateRange[0]))
								isOutOfRangeStart = true;
						}
						let isOutOfRangeEnd = false;
						if (max) {
							const maxTimestamp = dayjs(max);
							isOutOfRangeEnd = maxTimestamp.isBefore(dateRange[1]);
							let timestamp = maxTimestamp;
							while (timestamp.isBefore(dateRange[1])) {
								timestamp = timestamp.add(5, 'minute');
								if (!combinedData[FORECAST_KEY]) {
									combinedData[FORECAST_KEY] = [];
								}
								combinedData[FORECAST_KEY].push({ x: timestamp.valueOf(), y: null });
							}
						}
						const series: Highcharts.SeriesOptionsType[] = formatSeriesData(
							combinedData,
							colorArr,
						) as any;

						const mt = selectedAssetItem?.measurementTarget ?? {};

						const high = mt.thresholds?.high ?? {};
						const low = mt.thresholds?.low ?? {};

						setThresholds({
							atRiskHigh: toNumber(mt?.hcritical ?? high.risk),
							impactedHigh: toNumber(mt?.hhCritical ?? high.impacted),
							atRiskLow: toNumber(mt?.lowCritical ?? low.risk),
							impactedLow: toNumber(mt?.llowCritical ?? low.impacted),
							unit: mt.valueUnit ?? (mt?.config?.isPrimary ? mt?.config?.unit : ''),
						});

						const { atRiskHigh, atRiskLow, impactedHigh, impactedLow } = {
							atRiskHigh: Number(mt?.hcritical ?? high.risk),
							impactedHigh: Number(mt?.hhCritical ?? high.impacted),
							atRiskLow: Number(mt?.lowCritical ?? low.risk),
							impactedLow: Number(mt?.llowCritical ?? low.impacted),
						};

						if (Number.isFinite(atRiskHigh))
							series.push(buildThresholdSeries('At risk threshold', atRiskHigh as number, true));
						if (Number.isFinite(impactedHigh))
							series.push(buildThresholdSeries('Impacted threshold', impactedHigh as number, true));
						//TODO: bring back when discussion on display is concluded
						// if (Number.isFinite(atRiskLow))
						// 	series.push(buildThresholdSeries('At risk threshold', atRiskLow as number, false));
						// if (Number.isFinite(impactedLow))
						// 	series.push(buildThresholdSeries('Impacted threshold', impactedLow as number, false));

						const hasForecast = selectedAssetItem.measurementTarget?.hasForecast === true;

						// Notice logic
						if (!hasForecast || !shouldFetchForecast) {
							setNotice({
								variant: 'no_forecast',
								text: 'There’s currently no forecast data for this asset parameter.',
							});
							setNoticeDismissed(false);
						} else if (!forecastHasValues) {
							setNotice({
								variant: 'engine_fails',
								text: 'Forecast unavailable, multiple decision-engine checks failed for this asset.',
							});
							setNoticeDismissed(false);
						} else {
							setNotice({ variant: 'none' });
							setNoticeDismissed(false);
						}

						setChartOptions({
							chart: {
								height: 334,
								backgroundColor: 'rgba(0, 0, 0, 0)',
							},
							title: {
								text: '',
							},
							xAxis: {
								labels: {
									formatter: ({ value, isFirst, isLast }) =>
										isFirst || isLast
											? dayjs(value).format('D MMM HH:mm')
											: dayjs(value).format('HH:mm'),
								},
								crosshair: {
									width: 1,
									color: 'rgba(0,0,0,0.25)',
									dashStyle: 'Solid',
									zIndex: 5,
								},
							},
							yAxis: {
								labels: { formatter: ({ value }) => `${value}${unit}` },
								title: undefined,
							},
							series,
							credits: {
								enabled: false,
							},
							exporting: {
								enabled: false,
							},
							tooltip: {
								shared: false,
								useHTML: true,
								backgroundColor: '#ffffff',
								borderColor: 'rgba(0,0,0,0.25)',
								borderRadius: 8,
								shadow: true,
								padding: 10,
								style: { color: '#111827', fontSize: '12px', fontWeight: '600' },
								formatter: function () {
									const x = Number(this.x);
									const y = Number(this.y);
									const when = dayjs(x).format('D MMM YYYY HH:mm');
									const assetName =
										selectedAssetItem?.name ?? selectedAssetItem?.measurementTarget?.name ?? '';
									const paramLabel =
										selectedAssetItem?.measurementTarget?.measurementTargetName ??
										selectedAssetItem?.measurementTarget?.config.type ??
										'';
									const unit =
										selectedAssetItem?.measurementTarget?.unit ??
										(selectedAssetItem?.measurementTarget?.config?.isPrimary
											? selectedAssetItem?.measurementTarget?.config?.unit
											: '');
									const range = this.point?.custom?.range;
									const hasRange =
										Array.isArray(range) &&
										range.length === 2 &&
										Number.isFinite(range[0]) &&
										Number.isFinite(range[1]);

									const valueHtml = hasRange
										? `<span style="font-weight:800; color:#0A8399; font-size:14px;">
											${paramLabel}: ${range![0].toFixed(3)} – ${range![1].toFixed(3)}${unit ? ` ${unit}` : ''}
										</span>`
										: `<span style="font-weight:800; color:#0A8399; font-size:14px;">
											${paramLabel}: ${Number.isFinite(y) ? y.toFixed(3) : y}${unit ? ` ${unit}` : ''}
										</span>`;
									return `

                                    <div style="min-width:220px;line-height:1.4;font-weight:600;">
										<div style="color:#374151;font-size:13px;margin-bottom:6px">${when}</div>
											<div style="display:flex;align-items:flex-start;gap:10px;margin:6px 0;">
												<span style="display:inline-block;width:15px;height:15px;border-radius:3px;margin-top:3px;background:${colorArr[1]};"></span>
												<div>
													<div style="display:block">
														${valueHtml}
														<p style="font-weight:500; font-size: 12px; color: #023E65">${assetName}</p>
													</div>
												</div>
											</div>
									</div>`;
								},
							},
						});
					})
					.catch((error) => {
						console.error('Error fetching data:', error);
						setChartOptions(undefined);
						setChartError('Failed to load chart data.');
					})
					.finally(() => {
						setLoadingChart(false);
					});
			} catch {}
		}
	}, [
		selectedAssetItem?.measurementTarget?.measurementTargetId ??
			selectedAssetItem?.measurementTarget?.id,
		dateRange,
	]);

	useEffect(() => {
		if (selectedAssetItem == null) setChartOptions(undefined);
	}, [selectedAssetItem]);

	const pickerRef = useRef<{ close: () => void }>(null);

	const handleCompare = () => {
		pickerRef.current?.close();
		setShowComparisonGraph(true);
	};

	if (!selectedAssetItem) return null;

	const unit = mt.valueUnit || mt?.config?.isPrimary ? mt?.config?.unit || '' : '';
	const curMeasureValue = roundTo(
		mt?.currentValue ??
			mt?.dataSources?.lastReading?.value ??
			(Array.isArray(mt?.dataSources) ? mt.dataSources[0]?.lastReading?.value : undefined),
		2,
	);

	const hasCurrentMeasure =
		curMeasureValue !== undefined &&
		curMeasureValue !== null &&
		Number.isFinite(Number(curMeasureValue));

	const currentMeasurementDisplay = hasCurrentMeasure
		? `${curMeasureValue} ${unit}`
		: 'No current measurement available';

	type SiteAlertKind =
		| 'BLOCKAGE'
		| 'ANOMALY'
		| 'PUMP'
		| 'BLOCKAGE_ANOMALY'
		| 'BLOCKAGE_PUMP'
		| 'ANOMALY_PUMP'
		| 'BLOCKAGE_ANOMALY_PUMP';

	const siteAlert = selectedAssetItem?.siteAlert as
		| { kind?: SiteAlertKind; url?: string | null }
		| undefined;

	const siteAlertLabel = (() => {
		switch (siteAlert?.kind) {
			case 'BLOCKAGE':
				return 'Blockage alert';
			case 'ANOMALY':
				return 'Anomaly alert';
			case 'PUMP':
				return 'Pump alert';
			case 'BLOCKAGE_ANOMALY':
				return 'Blockage & anomaly alert';
			case 'BLOCKAGE_PUMP':
				return 'Blockage & pump alert';
			case 'ANOMALY_PUMP':
				return 'Anomaly & pump alert';
			case 'BLOCKAGE_ANOMALY_PUMP':
				return 'Blockage, anomaly & pump alert';
			default:
				return null;
		}
	})();

	const isPumpOnly = siteAlert?.kind === 'PUMP';

	const siteAlertIsBlock =
		siteAlert?.kind === 'BLOCKAGE' ||
		siteAlert?.kind === 'BLOCKAGE_ANOMALY' ||
		siteAlert?.kind === 'BLOCKAGE_PUMP' ||
		siteAlert?.kind === 'BLOCKAGE_ANOMALY_PUMP';

	if (!selectedAssetItem) return null;

	return (
		selectedAssetItem && (
			<div
				data-testid="asset-info-modal"
				id="asset-info-modal"
				tabIndex={-1}
				aria-hidden="true"
				style={{ backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
				className="flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-[101] justify-center items-center w-full md:inset-0 h-screen max-h-full"
			>
				<div className="relative p-4 w-full max-w-6xl max-h-full">
					{/* <!-- Modal content --> */}
					<div className="relative bg-white rounded-lg shadow-sm">
						<ModalHeader
							selectedAssetItem={selectedAssetItem}
							onModalClose={onModalClose}
							iconUrl={iconUrl}
						/>

						{/* two-column body */}
						<div className="grid grid-cols-1 md:grid-cols-12 gap-2">
							{/* Left column: Info + capacity cards */}
							<div className="md:col-span-5">
								<ModalRowContainer>
									<h5 className="font-bold mb-2 text-[14px]">Information</h5>
									{/* --- alert banner (only if this asset has a linked site alert) --- */}
									{siteAlertLabel &&
										(isPumpOnly ? (
											<div className="flex items-center justify-between rounded-md border border-[#077898] bg-[#F4FBFF] px-3 py-2 mb-3 text-[#535662]">
												<span className="flex items-center gap-1">
													<svg
														className="w-4 h-4 mr-1"
														width="14"
														height="14"
														viewBox="0 0 14 14"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M7.00004 3.66634C6.82323 3.66634 6.65366 3.73658 6.52864 3.8616C6.40361 3.98663 6.33337 4.1562 6.33337 4.33301V6.99967C6.33337 7.17649 6.40361 7.34606 6.52864 7.47108C6.65366 7.5961 6.82323 7.66634 7.00004 7.66634C7.17685 7.66634 7.34642 7.5961 7.47145 7.47108C7.59647 7.34606 7.66671 7.17649 7.66671 6.99967V4.33301C7.66671 4.1562 7.59647 3.98663 7.47145 3.8616C7.34642 3.73658 7.17685 3.66634 7.00004 3.66634ZM7.00004 8.99967C6.86819 8.99967 6.73929 9.03877 6.62966 9.11203C6.52003 9.18528 6.43458 9.2894 6.38412 9.41122C6.33366 9.53304 6.32046 9.66708 6.34618 9.7964C6.37191 9.92572 6.4354 10.0445 6.52864 10.1377C6.62187 10.231 6.74066 10.2945 6.86998 10.3202C6.9993 10.3459 7.13335 10.3327 7.25516 10.2823C7.37698 10.2318 7.4811 10.1464 7.55435 10.0367C7.62761 9.92709 7.66671 9.7982 7.66671 9.66634C7.66671 9.48953 7.59647 9.31996 7.47145 9.19494C7.34642 9.06991 7.17685 8.99967 7.00004 8.99967ZM13.4734 4.03967L9.96004 0.526341C9.83229 0.404519 9.66322 0.335463 9.48671 0.333008H4.51337C4.33686 0.335463 4.1678 0.404519 4.04004 0.526341L0.526707 4.03967C0.404885 4.16743 0.33583 4.3365 0.333374 4.51301V9.48634C0.33583 9.66285 0.404885 9.83192 0.526707 9.95967L4.04004 13.473C4.1678 13.5948 4.33686 13.6639 4.51337 13.6663H9.48671C9.66322 13.6639 9.83229 13.5948 9.96004 13.473L13.4734 9.95967C13.5952 9.83192 13.6643 9.66285 13.6667 9.48634V4.51301C13.6643 4.3365 13.5952 4.16743 13.4734 4.03967ZM12.3334 9.20634L9.20671 12.333H4.79337L1.66671 9.20634V4.79301L4.79337 1.66634H9.20671L12.3334 4.79301V9.20634Z"
															fill="#535662"
															stroke="#535662"
															strokeWidth="0.25"
														/>
													</svg>
													<span>{siteAlertLabel}</span>
												</span>

												{siteAlert?.url && (
													<a
														href={siteAlert.url}
														target="_blank"
														rel="noopener noreferrer"
														className="underline text-[14px] text-[#077898] hover:text-[#055f73] flex items-center gap-1"
													>
														<svg
															width="14"
															height="14"
															viewBox="0 0 13 12"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
															className="h-4 w-4"
														>
															<path
																d="M2 9.46777V2.96777C2 2.41549 2.44772 1.96777 3 1.96777H6.5C6.77614 1.96777 7 2.19163 7 2.46777C7 2.74392 6.77614 2.96777 6.5 2.96777H3V9.46777H9.5V5.96777C9.5 5.69163 9.72386 5.46777 10 5.46777C10.2761 5.46777 10.5 5.69163 10.5 5.96777V9.46777C10.5 10.0201 10.0523 10.4678 9.5 10.4678H3C2.44772 10.4678 2 10.0201 2 9.46777Z"
																fill="#077898"
															/>
															<path
																d="M11.0832 1.14685C11.033 1.12471 10.9789 1.1127 10.9241 1.1115L7.97781 1.1115C7.92287 1.1115 7.86847 1.12232 7.81771 1.14334C7.76695 1.16437 7.72083 1.19519 7.68198 1.23404C7.60352 1.3125 7.55944 1.41891 7.55944 1.52987C7.55944 1.58481 7.57026 1.63921 7.59129 1.68997C7.61231 1.74073 7.64313 1.78685 7.68198 1.8257C7.76044 1.90416 7.86685 1.94824 7.97781 1.94824L9.91941 1.94529L6.26897 5.59573C6.19083 5.67387 6.14693 5.77985 6.14693 5.89036C6.14693 6.00087 6.19083 6.10685 6.26897 6.18499C6.34711 6.26313 6.45309 6.30703 6.5636 6.30703C6.67411 6.30703 6.78009 6.26313 6.85823 6.18499L10.5087 2.53455L10.5057 4.47615C10.5055 4.53115 10.5162 4.58566 10.5371 4.63652C10.5581 4.68738 10.5889 4.73359 10.6278 4.77248C10.6667 4.81138 10.7129 4.84219 10.7637 4.86313C10.8146 4.88408 10.8691 4.89474 10.9241 4.89452C10.9791 4.89474 11.0336 4.88408 11.0845 4.86313C11.1353 4.84219 11.1815 4.81138 11.2204 4.77248C11.2593 4.73359 11.2901 4.68738 11.3111 4.63652C11.332 4.58566 11.3427 4.53115 11.3425 4.47615L11.3425 1.52987C11.3413 1.47503 11.3293 1.42096 11.3071 1.37077C11.2648 1.26957 11.1844 1.18912 11.0832 1.14685Z"
																fill="#077898"
															/>
														</svg>
														<span>Details</span>
													</a>
												)}
											</div>
										) : (
											<div
												className={`flex items-center justify-between rounded-md px-3 py-2 mb-3 text-[#535662] ${
													siteAlertIsBlock ? 'bg-red-100' : 'bg-amber-100'
												}`}
											>
												<span className="flex items-center">
													<svg
														className="w-4 h-4 mr-1"
														width="14"
														height="14"
														viewBox="0 0 14 14"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M7.00004 3.66634C6.82323 3.66634 6.65366 3.73658 6.52864 3.8616C6.40361 3.98663 6.33337 4.1562 6.33337 4.33301V6.99967C6.33337 7.17649 6.40361 7.34606 6.52864 7.47108C6.65366 7.5961 6.82323 7.66634 7.00004 7.66634C7.17685 7.66634 7.34642 7.5961 7.47145 7.47108C7.59647 7.34606 7.66671 7.17649 7.66671 6.99967V4.33301C7.66671 4.1562 7.59647 3.98663 7.47145 3.8616C7.34642 3.73658 7.17685 3.66634 7.00004 3.66634ZM7.00004 8.99967C6.86819 8.99967 6.73929 9.03877 6.62966 9.11203C6.52003 9.18528 6.43458 9.2894 6.38412 9.41122C6.33366 9.53304 6.32046 9.66708 6.34618 9.7964C6.37191 9.92572 6.4354 10.0445 6.52864 10.1377C6.62187 10.231 6.74066 10.2945 6.86998 10.3202C6.9993 10.3459 7.13335 10.3327 7.25516 10.2823C7.37698 10.2318 7.4811 10.1464 7.55435 10.0367C7.62761 9.92709 7.66671 9.7982 7.66671 9.66634C7.66671 9.48953 7.59647 9.31996 7.47145 9.19494C7.34642 9.06991 7.17685 8.99967 7.00004 8.99967ZM13.4734 4.03967L9.96004 0.526341C9.83229 0.404519 9.66322 0.335463 9.48671 0.333008H4.51337C4.33686 0.335463 4.1678 0.404519 4.04004 0.526341L0.526707 4.03967C0.404885 4.16743 0.33583 4.3365 0.333374 4.51301V9.48634C0.33583 9.66285 0.404885 9.83192 0.526707 9.95967L4.04004 13.473C4.1678 13.5948 4.33686 13.6639 4.51337 13.6663H9.48671C9.66322 13.6639 9.83229 13.5948 9.96004 13.473L13.4734 9.95967C13.5952 9.83192 13.6643 9.66285 13.6667 9.48634V4.51301C13.6643 4.3365 13.5952 4.16743 13.4734 4.03967ZM12.3334 9.20634L9.20671 12.333H4.79337L1.66671 9.20634V4.79301L4.79337 1.66634H9.20671L12.3334 4.79301V9.20634Z"
															fill="#535662"
															stroke="#535662"
															strokeWidth="0.25"
														/>
													</svg>

													{siteAlertLabel}
												</span>
												{siteAlert?.url && (
													<a
														href={siteAlert.url}
														target="_blank"
														rel="noopener noreferrer"
														className="underline text-[14px] text-[#077898] hover:text-[#055f73] flex items-center gap-1"
													>
														<svg
															width="16"
															height="16"
															viewBox="0 0 13 12"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
															className="h-4 w-4"
														>
															<path
																d="M2 9.46777V2.96777C2 2.41549 2.44772 1.96777 3 1.96777H6.5C6.77614 1.96777 7 2.19163 7 2.46777C7 2.74392 6.77614 2.96777 6.5 2.96777H3V9.46777H9.5V5.96777C9.5 5.69163 9.72386 5.46777 10 5.46777C10.2761 5.46777 10.5 5.69163 10.5 5.96777V9.46777C10.5 10.0201 10.0523 10.4678 9.5 10.4678H3C2.44772 10.4678 2 10.0201 2 9.46777Z"
																fill="#077898"
															/>
															<path
																d="M11.0832 1.14685C11.033 1.12471 10.9789 1.1127 10.9241 1.1115L7.97781 1.1115C7.92287 1.1115 7.86847 1.12232 7.81771 1.14334C7.76695 1.16437 7.72083 1.19519 7.68198 1.23404C7.60352 1.3125 7.55944 1.41891 7.55944 1.52987C7.55944 1.58481 7.57026 1.63921 7.59129 1.68997C7.61231 1.74073 7.64313 1.78685 7.68198 1.8257C7.76044 1.90416 7.86685 1.94824 7.97781 1.94824L9.91941 1.94529L6.26897 5.59573C6.19083 5.67387 6.14693 5.77985 6.14693 5.89036C6.14693 6.00087 6.19083 6.10685 6.26897 6.18499C6.34711 6.26313 6.45309 6.30703 6.5636 6.30703C6.67411 6.30703 6.78009 6.26313 6.85823 6.18499L10.5087 2.53455L10.5057 4.47615C10.5055 4.53115 10.5162 4.58566 10.5371 4.63652C10.5581 4.68738 10.5889 4.73359 10.6278 4.77248C10.6667 4.81138 10.7129 4.84219 10.7637 4.86313C10.8146 4.88408 10.8691 4.89474 10.9241 4.89452C10.9791 4.89474 11.0336 4.88408 11.0845 4.86313C11.1353 4.84219 11.1815 4.81138 11.2204 4.77248C11.2593 4.73359 11.2901 4.68738 11.3111 4.63652C11.332 4.58566 11.3427 4.53115 11.3425 4.47615L11.3425 1.52987C11.3413 1.47503 11.3293 1.42096 11.3071 1.37077C11.2648 1.26957 11.1844 1.18912 11.0832 1.14685Z"
																fill="#077898"
															/>
														</svg>
														<span>Details</span>
													</a>
												)}
											</div>
										))}
									<AssetValuePair
										keyText={ASSET_ITEM_LABELS.ASSET_TYPE}
										value={capitaliseWord(selectedAssetItem?.type?.toLowerCase())}
									/>
									<AssetValuePair
										keyText={ASSET_ITEM_LABELS.LATITUDE}
										value={roundTo(Number(selectedAssetItem?.latitude), 5)}
									/>
									<AssetValuePair
										keyText={ASSET_ITEM_LABELS.LONGITUDE}
										value={roundTo(Number(selectedAssetItem?.longitude), 5)}
									/>
								</ModalRowContainer>

								<div className="px-5 py-5">
									<div className="flex justify-between items-center">
										<h5 className="font-bold mb-2 text-[14px]">Capacity</h5>
										{/* threshold banners */}
										{thresholdCross.status === 'impacted' && (
											<div className="justify-end text-right ms-auto">
												<div className="flex items-center bg-red-100 rounded-md px-3 py-2 mb-1">
													<svg
														className="size-5 mr-1"
														width="24"
														height="24"
														viewBox="0 0 19 17"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M9.1996 11.862C9.03478 11.862 8.87366 11.9109 8.73662 12.0025C8.59958 12.0941 8.49277 12.2242 8.4297 12.3765C8.36663 12.5287 8.35012 12.6963 8.38228 12.858C8.41443 13.0196 8.4938 13.1681 8.61034 13.2846C8.72689 13.4012 8.87537 13.4805 9.03702 13.5127C9.19867 13.5449 9.36623 13.5283 9.5185 13.4653C9.67077 13.4022 9.80092 13.2954 9.89249 13.1584C9.98406 13.0213 10.0329 12.8602 10.0329 12.6954C10.0329 12.4744 9.94514 12.2624 9.78885 12.1061C9.63257 11.9498 9.42061 11.862 9.1996 11.862ZM18.0913 13.087L11.3829 1.42038C11.1661 1.03164 10.8494 0.707841 10.4656 0.482433C10.0818 0.257026 9.64472 0.138184 9.1996 0.138184C8.75448 0.138184 8.31743 0.257026 7.93361 0.482433C7.54978 0.707841 7.23309 1.03164 7.01627 1.42038L0.349599 13.087C0.125262 13.4654 0.00471741 13.8962 0.000135667 14.3361C-0.00444608 14.7759 0.107097 15.2091 0.323503 15.5921C0.53991 15.975 0.853519 16.2941 1.23267 16.5171C1.61182 16.74 2.04309 16.859 2.48293 16.862H15.9163C16.3596 16.8664 16.7962 16.7528 17.1812 16.5328C17.5662 16.3128 17.8857 15.9944 18.1071 15.6102C18.3284 15.226 18.4436 14.7899 18.4409 14.3465C18.4381 13.9031 18.3174 13.4684 18.0913 13.087ZM16.6496 14.7537C16.5766 14.8837 16.47 14.9917 16.341 15.0664C16.212 15.1412 16.0653 15.1799 15.9163 15.1787H2.48293C2.33386 15.1799 2.18718 15.1412 2.0582 15.0664C1.92921 14.9917 1.82264 14.8837 1.7496 14.7537C1.67646 14.627 1.63795 14.4833 1.63795 14.337C1.63795 14.1908 1.67646 14.0471 1.7496 13.9204L8.41627 2.25371C8.4862 2.11721 8.59244 2.00265 8.7233 1.92266C8.85416 1.84267 9.00456 1.80034 9.15793 1.80034C9.3113 1.80034 9.4617 1.84267 9.59256 1.92266C9.72342 2.00265 9.82967 2.11721 9.8996 2.25371L16.6079 13.9204C16.6906 14.0452 16.7381 14.1901 16.7455 14.3397C16.7528 14.4892 16.7197 14.638 16.6496 14.7704V14.7537ZM9.1996 5.19538C8.97859 5.19538 8.76662 5.28317 8.61034 5.43945C8.45406 5.59573 8.36627 5.8077 8.36627 6.02871V9.36204C8.36627 9.58306 8.45406 9.79502 8.61034 9.9513C8.76662 10.1076 8.97859 10.1954 9.1996 10.1954C9.42061 10.1954 9.63257 10.1076 9.78885 9.9513C9.94514 9.79502 10.0329 9.58306 10.0329 9.36204V6.02871C10.0329 5.8077 9.94514 5.59573 9.78885 5.43945C9.63257 5.28317 9.42061 5.19538 9.1996 5.19538Z"
															fill="#535662"
														/>
													</svg>
													<span className="text-gray-700">Over impacted threshold</span>
												</div>
											</div>
										)}
										{thresholdCross.status === 'at_risk' && (
											<div className="justify-end text-right ms-auto">
												<div className="flex items-center bg-amber-100 rounded-md px-3 py-2 mb-1">
													<svg
														className="size-5 mr-1"
														width="24"
														height="24"
														viewBox="0 0 19 17"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M9.1996 11.862C9.03478 11.862 8.87366 11.9109 8.73662 12.0025C8.59958 12.0941 8.49277 12.2242 8.4297 12.3765C8.36663 12.5287 8.35012 12.6963 8.38228 12.858C8.41443 13.0196 8.4938 13.1681 8.61034 13.2846C8.72689 13.4012 8.87537 13.4805 9.03702 13.5127C9.19867 13.5449 9.36623 13.5283 9.5185 13.4653C9.67077 13.4022 9.80092 13.2954 9.89249 13.1584C9.98406 13.0213 10.0329 12.8602 10.0329 12.6954C10.0329 12.4744 9.94514 12.2624 9.78885 12.1061C9.63257 11.9498 9.42061 11.862 9.1996 11.862ZM18.0913 13.087L11.3829 1.42038C11.1661 1.03164 10.8494 0.707841 10.4656 0.482433C10.0818 0.257026 9.64472 0.138184 9.1996 0.138184C8.75448 0.138184 8.31743 0.257026 7.93361 0.482433C7.54978 0.707841 7.23309 1.03164 7.01627 1.42038L0.349599 13.087C0.125262 13.4654 0.00471741 13.8962 0.000135667 14.3361C-0.00444608 14.7759 0.107097 15.2091 0.323503 15.5921C0.53991 15.975 0.853519 16.2941 1.23267 16.5171C1.61182 16.74 2.04309 16.859 2.48293 16.862H15.9163C16.3596 16.8664 16.7962 16.7528 17.1812 16.5328C17.5662 16.3128 17.8857 15.9944 18.1071 15.6102C18.3284 15.226 18.4436 14.7899 18.4409 14.3465C18.4381 13.9031 18.3174 13.4684 18.0913 13.087ZM16.6496 14.7537C16.5766 14.8837 16.47 14.9917 16.341 15.0664C16.212 15.1412 16.0653 15.1799 15.9163 15.1787H2.48293C2.33386 15.1799 2.18718 15.1412 2.0582 15.0664C1.92921 14.9917 1.82264 14.8837 1.7496 14.7537C1.67646 14.627 1.63795 14.4833 1.63795 14.337C1.63795 14.1908 1.67646 14.0471 1.7496 13.9204L8.41627 2.25371C8.4862 2.11721 8.59244 2.00265 8.7233 1.92266C8.85416 1.84267 9.00456 1.80034 9.15793 1.80034C9.3113 1.80034 9.4617 1.84267 9.59256 1.92266C9.72342 2.00265 9.82967 2.11721 9.8996 2.25371L16.6079 13.9204C16.6906 14.0452 16.7381 14.1901 16.7455 14.3397C16.7528 14.4892 16.7197 14.638 16.6496 14.7704V14.7537ZM9.1996 5.19538C8.97859 5.19538 8.76662 5.28317 8.61034 5.43945C8.45406 5.59573 8.36627 5.8077 8.36627 6.02871V9.36204C8.36627 9.58306 8.45406 9.79502 8.61034 9.9513C8.76662 10.1076 8.97859 10.1954 9.1996 10.1954C9.42061 10.1954 9.63257 10.1076 9.78885 9.9513C9.94514 9.79502 10.0329 9.58306 10.0329 9.36204V6.02871C10.0329 5.8077 9.94514 5.59573 9.78885 5.43945C9.63257 5.28317 9.42061 5.19538 9.1996 5.19538Z"
															fill="#535662"
														/>
													</svg>
													<span className="text-gray-700">Over at risk threshold</span>
												</div>
											</div>
										)}
									</div>

									<div className="grid grid-cols-1 gap-3">
										<div className="bg-gray-100 rounded-lg px-6 py-2 justify-center text-center">
											<CapacityCard
												big={currentMeasurementDisplay}
												label={hasCurrentMeasure ? 'Current measurement' : ''}
												idPrefix="current"
											/>
										</div>
										<div className="grid grid-cols-2 gap-3">
											<CapacityCard
												big={formatIfUnsure(thresholds.atRiskHigh, unit)}
												label="At risk high threshold"
												idPrefix="at-high-risk"
											/>
											<CapacityCard
												big={formatIfUnsure(thresholds.impactedHigh, unit)}
												label="Impacted high threshold"
												idPrefix="impacted-high"
											/>
											{/* TODO: bring back when discussion on display is concluded */}
											{/* <CapacityCard
												big={formatIfUnsure(thresholds.atRiskLow, unit)}
												label="At risk low threshold"
												idPrefix="at-low-risk"
											/>
											<CapacityCard
												big={formatIfUnsure(thresholds.impactedLow, unit)}
												label="Impacted low threshold"
												idPrefix="impacted-low"
											/> */}
										</div>
									</div>
								</div>
							</div>

							{/* Right column: chart panel */}
							<div className="md:col-span-7 bg-[#f3f4f6] border-l">
								<div className="px-5 pt-3 pb-5">
									<div className="flex justify-between items-center">
										<h5 className="text-[14px] font-semibold">Depth variation over time</h5>
										<div className="flex items-center">
											<div className="max-w-[250px] me-2">
												<DateTimePicker
													id="asset-modal-date-time-picker"
													className="h-10"
													ref={pickerRef}
													shortCuts={shortCuts}
													value={pickerRange}
													allowClear={false}
													onCancel={() => {}}
													onSave={(dates: [Dayjs, Dayjs] | null) =>
														setDateRange(dates ?? undefined)
													}
												/>
											</div>

											<button className="custom-outline-btn" onClick={handleCompare}>
												<span>Compare</span>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
													className="ml-1"
												>
													<path
														d="M21 4.5H13.5V3C13.5 2.60218 13.342 2.22064 13.0607 1.93934C12.7794 1.65804 12.3978 1.5 12 1.5H3C2.60218 1.5 2.22064 1.65804 1.93934 1.93934C1.65804 2.22064 1.5 2.60218 1.5 3V18C1.5 18.3978 1.65804 18.7794 1.93934 19.0607C2.22064 19.342 2.60218 19.5 3 19.5H10.5V21C10.5 21.3978 10.658 21.7794 10.9393 22.0607C11.2206 22.342 11.6022 22.5 12 22.5H21C21.3978 22.5 21.7794 22.342 22.0607 22.0607C22.342 21.7794 22.5 21.3978 22.5 21V6C22.5 5.60218 22.342 5.22064 22.0607 4.93934C21.7794 4.65804 21.3978 4.5 21 4.5ZM3 11.25H7.6275L5.6925 13.1925L6.75 14.25L10.5 10.5L6.75 6.75L5.6925 7.8075L7.6275 9.75H3V3H12V18H3V11.25ZM12 21V19.5C12.3978 19.5 12.7794 19.342 13.0607 19.0607C13.342 18.7794 13.5 18.3978 13.5 18V6H21V12.75H16.3725L18.3075 10.8075L17.25 9.75L13.5 13.5L17.25 17.25L18.3075 16.1925L16.3725 14.25H21V21H12Z"
														fill="currentColor"
													/>
												</svg>
											</button>
										</div>
									</div>
								</div>

								<div
									data-testid="asset-modal-chart-wrapper"
									className="bg-gray-100 rounded-b-lg p-1"
									style={{
										minHeight: 336,
										width: '100%',
										overflow: 'hidden',
										position: 'relative',
									}}
								>
									{!loadingChart && !chartError && (
										<Chart
											options={chartOptions}
											showLegend={true}
											selectedAssetItem={selectedAssetItem}
										/>
									)}

									{!loadingChart && !chartError && notice.variant !== 'none' && !noticeDismissed && (
										<ChartNotice 
											variant={notice.variant} 
											text={notice.text} 
											onClose={() => setNoticeDismissed(true)}
										/>
									)}

									{loadingChart && (
										<div
											className="absolute inset-0 bg-white/60 flex items-center justify-center z-10"
											id="loading-chart-overlay"
										>
											<div className="flex items-center">
												<div className="h-6 w-6 rounded-full border-2 border-sky-300 border-t-sky-700 animate-spin" />
												<span className="ml-3 text-slate-700 font-semibold">Loading chart…</span>
											</div>
										</div>
									)}

									{!loadingChart && chartError && (
										<div className="chart-overlay">
											<div>
												<svg
													width="222"
													height="198"
													viewBox="0 0 222 198"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M125.297 60.0142C113.956 67.3386 102.059 74.1793 89.3136 78.0739C76.5686 81.9685 63.4424 82.3912 50.5122 81.3552C42.0673 80.6799 34.735 80.5527 27.6546 85.7899C20.6681 90.7826 15.4683 97.8839 12.8217 106.047C8.6601 120.264 19.7736 134.556 30.3713 142.889C46.2574 155.358 70.5955 155.58 89.8463 153.54C112.111 151.198 133.79 144.257 154.176 135.225C172.12 127.273 194.142 115.75 201.497 96.1255C208.257 78.0808 197.598 53.5163 184.82 40.4759C181.213 36.5192 176.28 34.0192 170.951 33.4466C157.585 32.5937 146.83 45.7172 136.723 52.4641C132.976 54.9671 129.169 57.5269 125.297 60.0142Z"
														fill="white"
													/>
													<path
														opacity="0.5"
														d="M113.325 180.94C151.709 180.94 182.825 178.925 182.825 176.44C182.825 173.955 151.709 171.94 113.325 171.94C74.9414 171.94 43.8252 173.955 43.8252 176.44C43.8252 178.925 74.9414 180.94 113.325 180.94Z"
														fill="#9194A3"
													/>
													<path
														d="M57.7441 52.2139V145.714H159.244M76.2441 123.714L94.7441 97.7139L108.244 107.214L116.244 97.7139L130.244 107.214L155.244 68.7139"
														stroke="#66C8EB"
														stroke-width="9"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<div className="text-[18px] text-[#374151]">Unable to display graph</div>
												<div className="text-[12px] text-[#6B7280]">
													Please refresh or try again later.
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
						{/* end two-column body */}
					</div>
				</div>
			</div>
		)
	);
};

export default AssetInfo;
